import { SubscriptionService, useApiGetter, useEntitledEmulators } from '@localstack/services';
import { Box, Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import { ReactElement } from 'react';

import { Permission, PlanFamily, ProductFeature } from '@localstack/types';
import { FeatureUnavailableBox } from '@localstack/ui';
import { ExternalLink } from '@localstack/constants';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { pricingRoute } from '~/util/pricing';

import { ManageSubscriptionCITokens } from './ManageCIToken';

export const CITokenList = (): ReactElement => {
  const { data: subscriptionCiTokens, isLoading: isApiKeysLoading } = useApiGetter(
    SubscriptionService,
    'listCiTokens',
    [],
  );
  const { can, hasFeature } = useAuthProvider();
  const emulators = useEntitledEmulators({ useAuthProvider });

  const canReadTokens = can(Permission.READ_CI_TOKENS);
  const hasSubscription = hasFeature(ProductFeature.FEATURE_LOCALSTACK_PRO);

  if (isApiKeysLoading) {
    return (
      <>
        <Grid item xs={12}>
          <Skeleton variant="rounded" height={240} />
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="rounded" height={240} />
        </Grid>
      </>
    );
  }

  if (!hasSubscription || !canReadTokens) {
    return (
      <Grid item xs={12}>
        {!hasSubscription ? (
          <FeatureUnavailableBox
            icon="ciAuthToken"
            variant="bg-centered"
            availableIn={{ family: PlanFamily.BASE_PLANS, emulators }}
            upgradeLink={pricingRoute(emulators)}
            docsLink={ExternalLink.DOCS_AUTH_TOKEN}
            title="CI Auth Tokens"
            description="CI Auth Tokens are designed for use in CI environments and other non-developer contexts. These tokens are stored in the workspace and can be managed by members with appropriate permissions."
          />
        ) : (
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                gap={1}
                paddingY={5}
              >
                <Typography variant="h6" align="center" fontWeight={600}>
                  CI Auth Tokens
                </Typography>
                <Typography variant="body1" align="center" color="text.secondary">
                  You don&apos;t have the permissions required to view or manage CI Auth Tokens. Reach out to the
                  workspace admin to review your permissions
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Grid>
    );
  }

  return (
    <>
      {subscriptionCiTokens?.map((token) => (
        <ManageSubscriptionCITokens subscriptionCITokens={token} key={token.subscription_id} />
      ))}
    </>
  );
};
